import React from 'react'
import styled from 'styled-components'
import {useTownhallTemplate} from 'Event/template/Townhall'
import {useHasTechCheck} from 'Event/EventProvider'
import {useAttendeeVariables} from 'Event'
import {Icon} from 'lib/fontawesome/Icon'
import Configurable, {
  MinContentConfigurable,
} from 'organization/Event/Configurable'
import StepIconConfig from 'Event/template/Townhall/check-in/StepIconConfig'
import StepLabelConfig from 'Event/template/Townhall/check-in/StepLabelConfig'
import {useHasWaiver} from 'Event/Step2/Waivers'
import {useRequiresPassword} from 'Event/Step1/SetPasswordForm'

type Step = 1 | 2 | 3

export default function StepIndicator(props: {step: Step; className?: string}) {
  const {checkIn} = useTownhallTemplate()
  const requiresPassword = useRequiresPassword()
  const hasWaiver = useHasWaiver()
  const hasTechCheck = useHasTechCheck()

  return (
    <Box className={props.className}>
      <IconContainer>
        <StepBox
          step={1}
          currentStep={props.step}
          icon={checkIn.step1Icon}
          showing={requiresPassword}
          isLast={!hasWaiver && !hasTechCheck}
        />
        <StepBox
          step={2}
          currentStep={props.step}
          icon={checkIn.step2Icon}
          showing={hasWaiver}
          isLast={!hasTechCheck}
        />
        <StepBox
          step={3}
          currentStep={props.step}
          icon={checkIn.step3Icon}
          showing={hasTechCheck}
          isLast
        />
      </IconContainer>
      <Configurable>
        <StepLabelConfig step={props.step} />
        <LabelContainer>
          <CheckInLabel />
          <StepLabel step={props.step} />
        </LabelContainer>
      </Configurable>
    </Box>
  )
}

function CheckInLabel() {
  const {
    checkIn: {title: checkInLabel, titleColor},
  } = useTownhallTemplate()

  const v = useAttendeeVariables()

  return <Label color={titleColor}>{v(checkInLabel)}</Label>
}

function StepLabel(props: {step: Step}) {
  const {checkIn} = useTownhallTemplate()

  const labels = {
    1: checkIn.step1Label,
    2: checkIn.step2Label,
    3: checkIn.step3Label,
  }

  const label = labels[props.step]

  const v = useAttendeeVariables()
  return <Label color={checkIn.stepLabelColor}>&nbsp;{v(label)}</Label>
}

function StepIcon(props: {
  icon: string
  isActive: boolean
  'aria-label'?: string
}) {
  const {isActive} = props
  const {checkIn} = useTownhallTemplate()
  const color = isActive ? checkIn.stepIconColor : checkIn.inActiveColor

  return (
    <StyledIcon
      iconClass={props.icon}
      color={color}
      aria-label={props['aria-label']}
    />
  )
}

function Divider(props: {isActive: boolean; showing: boolean}) {
  const {isActive, showing} = props
  const {checkIn} = useTownhallTemplate()
  const color = isActive ? checkIn.stepIconColor : checkIn.inActiveColor

  if (!showing) {
    return null
  }

  return <DividerLine color={color} />
}

function StepBox(props: {
  step: Step
  currentStep: number
  icon: string
  showing: boolean
  isLast?: boolean
}) {
  const {showing, isLast, step, currentStep, icon} = props

  if (!showing) {
    return null
  }

  const isIconActive = currentStep >= step
  const isDividerActive = currentStep > step

  return (
    <>
      <MinContentConfigurable>
        <StepIconConfig step={step} />
        <StepIcon
          icon={icon}
          isActive={isIconActive}
          aria-label={`step ${step} icon`}
        />
      </MinContentConfigurable>
      <Divider isActive={isDividerActive} showing={!isLast} />
    </>
  )
}

const Label = styled.h4<{
  color?: string
}>`
  ${(props) => (props.color ? `color: ${props.color};` : '')}
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  line-height: 36px;
`

const Box = styled.div`
  text-align: center;
`

const LabelContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${(props) => props.theme.spacing[5]};
  margin-top: ${(props) => props.theme.spacing[5]};
`

const IconContainer = styled.div<{
  horizontal?: boolean
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const StyledIcon = styled(Icon)`
  font-size: 40px;
`

const DividerLine = styled.div<{color: string}>`
  background: ${(props) => props.color};
  width: 120px;
  height: 4px;
  margin: 8px;
  border-radius: 10px;
`
